import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { platformAPI } from "../services/api";

function DeleteDialog({ template, onClose, onDeleted }) {
  const [impact, setImpact] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    platformAPI.templateDeleteImpact(template.id).then(({ data }) => setImpact(data));
  }, [template.id]);

  const confirmDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await platformAPI.deleteTemplate(template.id);
      onDeleted();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete template");
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 max-w-md w-full">
        <h3 className="font-semibold text-gray-800 mb-3">Delete "{template.name}"?</h3>

        {!impact ? (
          <p className="text-gray-500 text-sm">Checking what this affects…</p>
        ) : (
          <div className="text-sm text-gray-600 space-y-2 mb-4">
            <p>
              This template has <strong>{impact.item_count}</strong> product
              {impact.item_count === 1 ? "" : "s"} — deleting it will permanently lose all of them.
            </p>
            {impact.shops.length > 0 && (
              <p className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
                It was already copied into {impact.shops.length} shop
                {impact.shops.length === 1 ? "" : "s"} ({impact.shops.map((s) => s.shop_name).join(", ")}).
                Their products are already theirs and will <strong>not</strong> be removed — only the
                link back to this template will be lost.
              </p>
            )}
          </div>
        )}

        {error && <p className="text-red-600 text-sm mb-2">{error}</p>}

        <div className="flex gap-3 justify-end">
          <button onClick={onClose} className="text-gray-500 hover:underline">Cancel</button>
          <button
            onClick={confirmDelete}
            disabled={busy || !impact}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
          >
            {busy ? "Deleting…" : "Delete template"}
          </button>
        </div>
      </div>
    </div>
  );
}

const PROBLEM_TEXT = {
  empty: "has no products",
  missing_name: "has no name",
  name_too_long: "name is too long",
  missing_category: "has no valid category",
  missing_name_fr: "has no French name",
  missing_name_ar: "has no Arabic name",
  duplicate: "is listed twice",
  no_image: "has no picture",
  no_description: "has no description",
};

/** What the approval check found: errors block, warnings are only worth knowing. */
function TemplateProblems({ report }) {
  const errors = report.errors || [];
  const warnings = report.warnings || [];

  return (
    <div className="basis-full text-sm">
      {errors.length === 0 ? (
        <p className="text-green-700">
          ✓ Ready to approve ({report.items} products
          {warnings.length ? `, ${warnings.length} warnings` : ""}).
        </p>
      ) : (
        <>
          <p className="font-medium text-red-700">
            {errors.length} problem{errors.length === 1 ? "" : "s"} to fix before this can be approved:
          </p>
          <ul className="mt-1 list-disc ps-5 text-red-700 max-h-48 overflow-y-auto">
            {errors.map((e, index) => (
              <li key={`${e.id}-${index}`}>
                {e.name || "Template"} {PROBLEM_TEXT[e.problem] || e.problem}
                {e.detail ? ` (${e.detail})` : ""}
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

export default function ProductTemplates() {
  const [templates, setTemplates] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = () =>
    platformAPI.listTemplates(statusFilter || undefined).then(({ data }) => setTemplates(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const create = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setError("");
    try {
      await platformAPI.createTemplate(name.trim());
      setName("");
      await load();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create template");
    } finally {
      setBusy(false);
    }
  };

  // Why a template cannot be approved yet, shown under it
  const [problems, setProblems] = useState({});

  const approve = async (t) => {
    try {
      await platformAPI.approveTemplate(t.id);
      setProblems((current) => ({ ...current, [t.id]: null }));
      await load();
    } catch (err) {
      const data = err.response?.data;
      if (data?.code === "TEMPLATE_INVALID") {
        setProblems((current) => ({ ...current, [t.id]: data.details }));
      } else {
        setError(data?.message || data?.error || "Failed to approve");
      }
    }
  };

  const check = async (t) => {
    try {
      const { data } = await platformAPI.templateValidation(t.id);
      setProblems((current) => ({ ...current, [t.id]: data }));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to check");
    }
  };

  const archive = async (t) => {
    await platformAPI.archiveTemplate(t.id);
    await load();
  };

  const statusColor = {
    draft: "bg-gray-100 text-gray-600",
    approved: "bg-green-100 text-green-700",
    archived: "bg-red-100 text-red-600",
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Product templates</h1>
        <select
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">All statuses</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="archived">Archived</option>
        </select>
      </div>

      <form onSubmit={create} className="bg-white rounded-xl shadow p-4 mb-6 flex gap-3">
        <input
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          placeholder='New template name, e.g. "Starter juice bundle"'
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <button
          type="submit"
          disabled={busy}
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-4 py-2 rounded-lg disabled:opacity-50"
        >
          Create
        </button>
      </form>
      {error && <p className="text-red-600 text-sm mb-4">{error}</p>}

      <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
        {templates.map((t) => (
          <div key={t.id} className="px-4 py-3 flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <Link to={`/product-templates/${t.id}`} className="font-medium text-primary-700 hover:underline">
                {t.name}
              </Link>{" "}
              <span className={`px-2 py-0.5 rounded-full text-xs ${statusColor[t.status]}`}>
                {t.status}
              </span>
              <p className="text-xs text-gray-400">
                {t.item_count} product{t.item_count === 1 ? "" : "s"}
              </p>
            </div>
            <div className="space-x-3 shrink-0 text-sm">
              {t.status !== "approved" && (
                <>
                  <button onClick={() => check(t)} className="text-gray-600 hover:underline">
                    Check
                  </button>
                  <button onClick={() => approve(t)} className="text-green-700 hover:underline">
                    Approve
                  </button>
                </>
              )}
              {t.status !== "archived" && (
                <button onClick={() => archive(t)} className="text-gray-500 hover:underline">
                  Archive
                </button>
              )}
              <button onClick={() => setDeleteTarget(t)} className="text-red-600 hover:underline">
                Delete
              </button>
            </div>
            {problems[t.id] && <TemplateProblems report={problems[t.id]} />}
          </div>
        ))}
        {templates.length === 0 && (
          <p className="px-4 py-8 text-center text-gray-400">No templates yet.</p>
        )}
      </div>

      {deleteTarget && (
        <DeleteDialog
          template={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => {
            setDeleteTarget(null);
            load();
          }}
        />
      )}
    </div>
  );
}
